import { useLocation, useParams } from '@solidjs/router';
import { createEffect, createMemo, Match, on, Show, Switch, useContext } from 'solid-js';
import { AppContext } from '../../app-context-provider/app-context-provider';
import DynamicModuleGenerator from '../../dynamic-module-generator';
import { Section } from '../../grid-system/section/section';
import theme from '../../style/theme';
import { Block, ImageWordpress, PageCacheType } from '../../types/shared';
import { Heading } from '../../ui-components/heading/heading';
import { LockedContent } from '../../ui-components/layouts/locked-content';
import { StyledRelativeContainer } from '../../ui-components/utility-style-components/positioning';
import { NotFound } from '../404/404';
import { DocumentTypeFlag } from '../document-type-flag/document-type-flag';
import { CalendarIcon } from '../icons-library/calendar';
import { ListCardContent } from '../post-card/post-card-types';
import { RelatedPostsList } from '../related-posts-list/related-posts-list';
import { SeoSettings } from '../seo-settings/seo-settings';
import { Seo } from '../seo-settings/seo-settings-types';
import { ShareAndSave } from '../share-and-save/share-and-save';
import { StyledAuthor, StyledBlogWrapper, StyledBreadCrumbsWrapper, StyledCurrentPage, StyledDate, StyledDateText, StyledBlogAndNews, StyledBlogAndNewsOuter, StyledLink, StyledMetaInfo } from './blog-news.styles';
import { fetchRelatedBlogAndNews, GET_BLOG_NEWS_QUERY } from './fetch-blog-and-news';
import { formatTextDate } from '../../tools/date-format';
import { HCPWarning } from '../hcp-warning/hcp-warning';
import { Link } from '../../ui-components/link/link';
import removeAmpersand from '../../tools/remove-ampersand';
import { navigateToAnchor } from '../../tools/scroll-to-element';
import { UserWarning } from '../user-warning/user-warning';

export type BlogNewsData = {
    post_title: string;
    url: string;
    blocks: Block[];
    permalink: string;
    showRelatedPosts: boolean;
    relatedPostsPosition: 'sidebar' | 'bottom';
    date: string;
    author: string;
    type: string;
    listCardContent: ListCardContent;
    eventDate: string;
    categories: Category[];
    isHealthcareProfessional: boolean;
    isVisibleForPublic: boolean;
    tags: string[];
    id: number;
    seo: Seo;
    hideOnWeb?: boolean;
    meta?: BlogNewsMeta;
    disableBreadcrumbs?: boolean;
    breadcrumbTitle?: string;
};

export type BlogNewsMeta = {
    postTitle: string;
    description?: string;
    featuredImage?: ImageWordpress;
    categories?: Category[];
    extra?: {
        listCardContent?: ListCardContent;
        tags?: string[];
        isVisibleForPublic?: boolean;
        isHealthcareProfessional?: boolean;
        hideOnWeb?: boolean;
        date?: string;
        url?: string;
    };
};

export type BlogNewsListDataContainer = {
    key: string;
    meta?: BlogNewsMeta;
}

export type BlogNewsListCardData = {
    url: string;
    permalink: string;
    listCardContent?: Partial <ListCardContent>;
    categories?: Category[];
    isHealthcareProfessional?: boolean;
    isVisibleForPublic?: boolean;
    date?: string;
    hideOnWeb?: boolean;
}

export type Category = {
    termId: number;
    name: string;
    slug: string;
    parent: number;
    children: Category[];
};

export const BlogNews = () => {
    const params = useParams<{ postSlug: string }>();
    const location = useLocation();

    const {
        appStore,
        setCacheType,
        viewport,
        setStatusCode,
        localize,
        userState,
        pageRegistration,
        createCachedResource,
        siteInfo,
        blogsNewsListingPage,
        hcpBlogsNewsListingPage,
        headerSettings,
    } = useContext(AppContext);

    const isTablet = () => viewport.width <= theme.breakpoints.TABLET;
    const isMobile = () => viewport.width <= theme.breakpoints.MOBILE;
    const isOriginalTablet = () => viewport.width <= theme.breakpoints.ORIGINAL_TABLET;

    const showHcpHeader = () => headerSettings.activateHcpHeader && isHcpPost() && !isOriginalTablet();

    createEffect(
        on(
            () => location.hash,
            () => navigateToAnchor({
                id: location.hash.replace('#', ''),
                smallScreen: isMobile(),
                isCareSite: siteInfo.siteType === 'atos-care',
            })
        )
    );

    const [blogNewsContent] = createCachedResource(GET_BLOG_NEWS_QUERY, () => ({ key: `/blog-and-news/${params.postSlug}` }), true);

    const tags = () => blogNewsContent()?.blogNews?.content?.tags;
    const currentPostId = () => blogNewsContent()?.blogNews?.content?.id;
    const relatedBlogsNewsData = createMemo(() => fetchRelatedBlogAndNews(tags(), currentPostId()));

    const relatedBlogsAndNewsConfig = () => {
        if (!blogNewsContent()?.blogNews?.content) {
            return { show: false, position: null };
        }
        if (blogNewsContent()?.blogNews?.content.showRelatedPosts && blogNewsContent()?.blogNews?.content.relatedPostsPosition === 'sidebar') {
            return { show: true, position: 'sidebar' };
        }
        if (blogNewsContent()?.blogNews?.content.showRelatedPosts && blogNewsContent()?.blogNews?.content.relatedPostsPosition === 'bottom') {
            return { show: true, position: 'bottom' };
        }
        return { show: false, position: null };
    };

    const blogsAndNewsSectionTemplate = () => {
        return relatedBlogsAndNewsConfig()?.show
            && relatedBlogsAndNewsConfig().position === 'sidebar'
            && !isTablet() ? [8, 4] : [12];
    };

    const isHcpPost = () => {
        if (checkHcp()) {
            setCacheType(PageCacheType.none);
            return true;
        }
        setCacheType(PageCacheType.defaultCache);
        return false;
    };

    const checkHcp = () => {
        /**
         * "appStore.isHcp" behaves like a ratchet, once set to true, it can't be set to false,
         * unless the homepage is visited (which is a "patients" page, not a HCP page)
         */
        if (location.pathname === '/') {
            return false;
        }

        if (appStore.isHcp()) {
            return true;
        }

        return Boolean(blogNewsContent()?.blogNews?.content.isHealthcareProfessional);
    };

    const setHcp = () => {
        const isHcp = checkHcp();
        appStore.setHcp(isHcp);
        return null;
    };

    setHcp();

    createEffect(() => {
        setHcp();
    });

    const isHiddenForPublic = () => {
        return blogNewsContent()?.blogNews?.content.isHealthcareProfessional
            && !blogNewsContent()?.blogNews?.content.isVisibleForPublic
            && !userState.user
            && pageRegistration.url !== '';
    };

    const checkNotFound = () => {
        if (!blogNewsContent()?.blogNews?.content || hideOnWeb()) {
            // This only exists on SSR. We call it to set the right status from server.
            if (typeof setStatusCode === 'function') {
                setStatusCode(404);
            }
            return true;
        } else {
            if (typeof setStatusCode === 'function') {
                setStatusCode(200);
            }
            return false;
        }
    };

    const hideOnWeb = () => blogNewsContent()?.blogNews?.content?.hideOnWeb && siteInfo.target === 'live';

    const getBreadcrumbTitle = () => {
        let title = blogNewsContent()?.blogNews?.content?.post_title;

        if (blogNewsContent()?.blogNews?.content?.useBreadcrumbTitle && blogNewsContent()?.blogNews?.content?.breadcrumbTitle !== '') {
            title = blogNewsContent()?.blogNews?.content?.breadcrumbTitle;
        }

        return removeAmpersand(title);
    };

    return (
        <>
            <StyledBlogWrapper adjustForHcpHeader={showHcpHeader()}>

                <Show when={checkNotFound() && !blogNewsContent.loading}>
                    <NotFound />
                </Show>

                <Show when={!checkNotFound() && !blogNewsContent.loading}>
                    <UserWarning isUserContent={blogNewsContent()?.blogNews?.content?.isUserContent} />
                    <HCPWarning isHcp={isHcpPost()} />
                    <SeoSettings seo={blogNewsContent()?.blogNews?.content.seo} title={blogNewsContent()?.blogNews?.content.post_title} />
                    <Section widthType="bgFull" templateShorthand={blogsAndNewsSectionTemplate() || [8, 4]}>
                        <StyledBlogAndNewsOuter>
                            <StyledBreadCrumbsWrapper>
                                <Switch>
                                    <Match when={isMobile()}>
                                        <Switch>
                                            <Match when={isHcpPost() && hcpBlogsNewsListingPage.url}>
                                                <Link
                                                    url={hcpBlogsNewsListingPage.url}
                                                    label={hcpBlogsNewsListingPage.title}
                                                    plainStyle={true}
                                                    arrowDirection={'left'}
                                                    arrowPosition='left'
                                                    size={'small'}
                                                />
                                            </Match>
                                            <Match when={!isHcpPost() && blogsNewsListingPage.url}>
                                                <Link
                                                    url={blogsNewsListingPage.url}
                                                    label={blogsNewsListingPage.title}
                                                    plainStyle={true}
                                                    arrowDirection={'left'}
                                                    arrowPosition='left'
                                                    size={'small'}
                                                />
                                            </Match>
                                        </Switch>
                                    </Match>


                                    <Match when={!isMobile()}>
                                        <Switch>
                                            <Match when={isHcpPost() && hcpBlogsNewsListingPage.url}>
                                                <StyledLink href={hcpBlogsNewsListingPage.url}>
                                                    {removeAmpersand(hcpBlogsNewsListingPage.title)} /
                                                </StyledLink>
                                                <StyledCurrentPage>{getBreadcrumbTitle()}</StyledCurrentPage>
                                            </Match>
                                            <Match when={!isHcpPost() && blogsNewsListingPage.url}>
                                                <StyledLink href={blogsNewsListingPage.url}>
                                                    {removeAmpersand(blogsNewsListingPage.title)} /
                                                </StyledLink>
                                                <StyledCurrentPage>{getBreadcrumbTitle()}</StyledCurrentPage>
                                            </Match>
                                        </Switch>
                                    </Match>
                                </Switch>
                            </StyledBreadCrumbsWrapper>

                            <StyledBlogAndNews>
                                <Show when={isHiddenForPublic()}>
                                    <LockedContent />
                                </Show>
                                <Heading tag={'h1'}>{blogNewsContent()?.blogNews?.content.post_title}</Heading>
                                <DocumentTypeFlag
                                    type={
                                        blogNewsContent()?.blogNews?.content.categories.length
                                            ? blogNewsContent()?.blogNews?.content.categories[0].name
                                            : localize('blog', 'Blog')
                                    }
                                    size="small"
                                />
                                <StyledMetaInfo>
                                    <StyledDate>
                                        <StyledRelativeContainer top={-5}>
                                            <CalendarIcon height={2.21} width={1.79} />
                                        </StyledRelativeContainer>
                                        <StyledDateText>
                                            {blogNewsContent()?.blogNews?.content?.date
                                                ? formatTextDate(blogNewsContent()?.blogNews?.content.date, siteInfo.key)
                                                : ''}
                                        </StyledDateText>
                                    </StyledDate>
                                    <Show when={blogNewsContent()?.blogNews?.content.listCardContent.displayHostName === 'yes'}>
                                        <StyledAuthor>
                                            {localize('author', 'Author')}: {blogNewsContent()?.blogNews?.content.listCardContent.hostName}
                                        </StyledAuthor>
                                    </Show>
                                </StyledMetaInfo>
                                <DynamicModuleGenerator
                                    content={blogNewsContent()?.blogNews?.content.blocks}
                                    permalink={blogNewsContent()?.blogNews?.content.permalink}
                                    pageTitle={blogNewsContent()?.blogNews?.content.post_title}
                                />
                                <ShareAndSave
                                    type="blog-and-news"
                                    permalink={blogNewsContent()?.blogNews?.content?.permalink}
                                    title={blogNewsContent()?.blogNews?.content?.post_title}
                                    data={blogNewsContent()?.blogNews?.content}
                                />
                            </StyledBlogAndNews>
                        </StyledBlogAndNewsOuter>

                        <Show
                            when={
                                relatedBlogsAndNewsConfig().show === true &&
                                relatedBlogsAndNewsConfig().position === 'sidebar' &&
                                relatedBlogsNewsData()?.length > 0
                            }
                        >
                            <RelatedPostsList
                                heading={localize('related-articles', 'Related Articles')}
                                orientation="vertical"
                                relatedPosts={relatedBlogsNewsData()}
                            />
                        </Show>
                    </Section>

                    <Show
                        when={
                            relatedBlogsAndNewsConfig().show === true && relatedBlogsAndNewsConfig().position === 'bottom' && relatedBlogsNewsData()?.length > 0
                        }
                    >
                        <Section widthType="bgFull" templateShorthand={[12]} isLayoutSection={true}>
                            <RelatedPostsList
                                heading={localize('related-articles', 'Related articles')}
                                // TODO: this should be horizontal when isue above is solved
                                orientation="horizontal"
                                relatedPosts={relatedBlogsNewsData()}
                            />
                        </Section>
                    </Show>
                </Show>
            </StyledBlogWrapper>
        </>
    );
};
